import { useEffect, useState } from "react";
import { Loader2, MessageSquare } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { useAuthStore } from "@/store/auth";
import { useScreenShare } from "../hooks/useScreenShare";
import { useScreenShareUiStore } from "../store/screenshareStore";
import { canUseScreenShare } from "../eligibility";
import { ScreenShareChatPanel } from "./ScreenShareChatPanel";
import { ScreenSharePicker } from "./ScreenSharePicker";
import { ScreenShareViewerPanel } from "./ScreenShareViewerPanel";

const BTN_PRIMARY =
  "rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700";
const BTN_SECONDARY =
  "rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm text-[var(--color-text-secondary)] hover:bg-black/5 dark:hover:bg-white/5";

/** Elapsed time as H:MM:SS (or MM:SS under an hour). */
function formatDuration(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/**
 * Live counter for the active share. It mounts only once sharing is truly live,
 * so its mount time is the moment the stream started; it ticks every second.
 */
function ShareDurationTimer() {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const startedAt = Date.now();
    const tick = () => setSeconds(Math.floor((Date.now() - startedAt) / 1000));
    const t = window.setInterval(tick, 1000);
    return () => window.clearInterval(t);
  }, []);
  return (
    <span className="tabular-nums text-xs font-medium text-[var(--color-text-primary)]">
      {formatDuration(seconds)}
    </span>
  );
}

/**
 * Mounted once globally (ProtectedLayout). Owns the single screen-share session:
 * the picker, the incoming-consent prompt, the sharer "you are sharing" bar, and
 * the viewer video panel. Every logged-in user mounts it so anyone can be asked
 * to share; the picker's content adapts to the viewer (CEO/director/dev) gate.
 */
export function ScreenShareHost() {
  const user = useAuthStore((s) => s.user);
  const pickerOpen = useScreenShareUiStore((s) => s.pickerOpen);
  const setPickerOpen = useScreenShareUiStore((s) => s.setPickerOpen);
  const ss = useScreenShare();
  const [sharerChatOpen, setSharerChatOpen] = useState(false);

  // Auto-dismiss a transient error (e.g. the other side rejected) after a moment.
  const { error, dismissError, phase } = ss;
  useEffect(() => {
    if (!error) return;
    const t = setTimeout(dismissError, 5000);
    return () => clearTimeout(t);
  }, [error, dismissError]);

  // Collapse the sharer chat when the session ends.
  useEffect(() => {
    if (phase === "idle") setSharerChatOpen(false);
  }, [phase]);

  if (!user) return null;

  const sharing = ss.role === "sharer" && ss.phase !== "idle" && !ss.incoming;
  const viewing = ss.role === "viewer" && ss.phase !== "idle";
  // The 1:1 GG chat needs both parties internal (contractors have no GG).
  const canChat = canUseScreenShare(user) && ss.peerInternal && !!ss.peerId;

  return (
    <>
      {/* Only internal users can INITIATE (a contractor can be a target/sharer
          but never opens the picker, so it never sees the internal roster). */}
      {canUseScreenShare(user) && (
        <ScreenSharePicker
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onStartView={(id) => ss.startViewRequest(id)}
          onStartShare={(id) => ss.startShareOffer(id)}
        />
      )}

      {/* Incoming request — consent prompt. */}
      <Modal open={!!ss.incoming} onClose={ss.rejectIncoming} title="Podgląd ekranu" size="sm">
        {ss.incoming && (
          <div className="space-y-4">
            <p className="text-sm text-[var(--color-text-primary)]">
              {ss.incoming.direction === "view_request" ? (
                <>
                  <b>{ss.incoming.from.name}</b> chce zobaczyć Twój ekran. Po zgodzie wskażesz
                  okno lub kartę do pokazania.
                </>
              ) : (
                <>
                  <b>{ss.incoming.from.name}</b> chce pokazać Ci swój ekran.
                </>
              )}
            </p>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={ss.rejectIncoming} className={BTN_SECONDARY}>
                Odrzuć
              </button>
              <button type="button" onClick={() => ss.acceptIncoming()} className={BTN_PRIMARY}>
                {ss.incoming.direction === "view_request" ? "Zezwól" : "Zobacz"}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Sharer — persistent status bar that walks the share through its phases:
          "calling" (waiting for the recipient to accept) → "connecting" (WebRTC
          negotiating) → "active" (live, with an elapsed-time counter). Carries an
          expandable 1:1 chat. */}
      {sharing && (
        <>
          {sharerChatOpen && canChat && ss.peerId && (
            <div className="fixed bottom-20 right-4 z-50 flex h-[26rem] w-80 flex-col overflow-hidden rounded-xl border border-[var(--color-border)] shadow-2xl">
              <ScreenShareChatPanel peerId={ss.peerId} />
            </div>
          )}
          <div
            className={`fixed bottom-4 right-4 z-50 flex items-center gap-3 rounded-xl border bg-[var(--color-bg-surface)] px-4 py-2.5 shadow-xl ${
              ss.phase === "active" ? "border-red-500" : "border-[var(--color-border)]"
            }`}
          >
            {ss.phase === "active" ? (
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-600" />
              </span>
            ) : (
              <Loader2 className="animate-spin text-red-500" size={16} />
            )}
            <span className="text-xs text-[var(--color-text-secondary)]">
              {ss.phase === "calling" ? (
                ss.peerBusy ? (
                  <>
                    <b>{ss.peerName}</b> jest w trakcie innej sesji
                  </>
                ) : (
                  <>
                    Oczekiwanie na akceptację: <b>{ss.peerName}</b>
                  </>
                )
              ) : ss.phase === "connecting" ? (
                <>
                  Łączenie z <b>{ss.peerName}</b>…
                </>
              ) : (
                <>
                  Udostępniasz ekran: <b>{ss.peerName}</b>
                </>
              )}
            </span>
            {ss.phase === "active" && <ShareDurationTimer />}
            {canChat && (
              <button
                type="button"
                onClick={() => setSharerChatOpen((o) => !o)}
                title="Czat 1:1"
                className={`rounded p-1 transition-colors hover:text-[var(--color-text-primary)] ${
                  sharerChatOpen ? "text-[var(--color-primary)]" : "text-[var(--color-text-muted)]"
                }`}
              >
                <MessageSquare size={15} />
              </button>
            )}
            <button type="button" onClick={ss.stop} className="rounded-md bg-red-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-red-700">
              {ss.phase === "active" ? "Zatrzymaj" : "Anuluj"}
            </button>
          </div>
        </>
      )}

      {/* Viewer — video once connected, otherwise a connecting indicator. */}
      {viewing &&
        (ss.phase === "active" && ss.remoteStream ? (
          <ScreenShareViewerPanel
            stream={ss.remoteStream}
            peerName={ss.peerName}
            peerId={ss.peerId}
            canChat={canChat}
            onClose={ss.stop}
          />
        ) : (
          <div className="fixed bottom-4 right-4 z-50 flex items-center gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-4 py-3 shadow-xl">
            <Loader2 className="animate-spin text-red-500" size={16} />
            <span className="text-xs text-[var(--color-text-secondary)]">
              {ss.phase === "calling" ? (
                ss.peerBusy ? (
                  <>
                    <b>{ss.peerName}</b> jest w trakcie innej sesji
                  </>
                ) : (
                  <>
                    Oczekiwanie na akceptację: <b>{ss.peerName}</b>
                  </>
                )
              ) : (
                <>
                  Łączenie z ekranem <b>{ss.peerName}</b>…
                </>
              )}
            </span>
            <button type="button" onClick={ss.stop} className="text-xs text-[var(--color-text-muted)] hover:text-red-500">
              Anuluj
            </button>
          </div>
        ))}

      {/* Transient error toast (e.g. the other side rejected). */}
      {ss.error && !sharing && !viewing && (
        <div className="fixed bottom-4 right-4 z-50 rounded-xl border border-red-500 bg-[var(--color-bg-surface)] px-4 py-2.5 text-xs text-[var(--color-text-secondary)] shadow-xl">
          {ss.error}
        </div>
      )}
    </>
  );
}
